import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Sparkles, AlertTriangle, CheckCircle2, Users, ArrowUpRight,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import GuideFeedbackTile from '../components/health/GuideFeedbackTile';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * v260.22 — Workspace-level Verification Health dashboard.
 *
 * Aggregates `field_provenance` + `field_verifications` across every
 * estimate the user can see. Surfaces:
 *   - Top metrics: imports pending, imports verified, % verified,
 *     stalled estimates count
 *   - Sortable table per estimate with click-through into the
 *     Requirements form
 *   - Top verifiers leaderboard
 */
export default function VerificationHealthPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('default'); // default | name | pending | verified | pct | days
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/requirements/verification-health`, {
          withCredentials: true,
        });
        if (!mounted) return;
        setData(res.data);
        setError('');
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const sortedEstimates = useMemo(() => {
    const arr = [...(data?.estimates || [])];
    if (sortKey === 'default') return arr;
    const dir = sortDir === 'desc' ? -1 : 1;
    const cmpFns = {
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      pending: (a, b) => (a.imports_pending - b.imports_pending),
      verified: (a, b) => (a.imports_verified - b.imports_verified),
      pct: (a, b) => (a.pct_verified - b.pct_verified),
      days: (a, b) => ((a.days_since_last_verify ?? -1) - (b.days_since_last_verify ?? -1)),
    };
    const fn = cmpFns[sortKey] || (() => 0);
    arr.sort((a, b) => dir * fn(a, b));
    return arr;
  }, [data, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortChevron = ({ field }) => {
    if (sortKey !== field) return null;
    return sortDir === 'desc' ? <ChevronDown className="inline w-3 h-3" /> : <ChevronUp className="inline w-3 h-3" />;
  };

  const goToVerify = (estimateId) => {
    navigate(`/estimator/${estimateId}/requirements`);
  };

  const renderStat = (label, value, accent = 'cyan') => (
    <Card>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </div>
        <div className={`text-2xl font-bold mt-1 text-${accent}-700 tabular-nums`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full px-6 py-6 space-y-5" data-testid="verification-health-page">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            <h1 className="text-xl font-bold text-slate-900">Verification Health</h1>
          </div>
          <p className="text-sm text-slate-600">
            Track human-verification status of AI-imported requirements across every estimate.
            Stalled rows (no verifications in {data?.stalled_days_threshold || 30}+ days with pending imports) bubble to the top.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-cyan-600 animate-spin" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-800" data-testid="verification-health-error">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="verification-health-stats">
              {renderStat('Estimates with AI imports', data.totals.estimates_with_imports, 'cyan')}
              {renderStat('Imports pending', data.totals.imports_pending, 'amber')}
              {renderStat('Imports verified', data.totals.imports_verified, 'emerald')}
              {renderStat('Stalled estimates', data.totals.stalled_estimates, data.totals.stalled_estimates > 0 ? 'rose' : 'slate')}
            </div>

            {/* Admin-only: in-app User Guide helpfulness + "No" voters to follow up */}
            <GuideFeedbackTile />

            {/* Overall progress */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-700">Overall verification</span>
                  <span className="text-sm font-bold text-cyan-700 tabular-nums">{data.totals.overall_pct_verified}%</span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${data.totals.overall_pct_verified === 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`}
                    style={{ width: `${data.totals.overall_pct_verified}%` }}
                  />
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  {data.totals.imports_verified.toLocaleString()} of {(data.totals.imports_pending + data.totals.imports_verified).toLocaleString()} AI-imported fields verified
                </div>
              </CardContent>
            </Card>

            {/* Estimates roster */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-slate-100">
                  <h2 className="text-sm font-semibold text-slate-800">Estimates with AI imports</h2>
                </div>
                {sortedEstimates.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-slate-500" data-testid="verification-health-empty">
                    <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                    No AI imports yet across any of your estimates. Try the
                    <strong> Import from Transcript </strong> button in the Requirements form.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                          <th className="px-4 py-2 font-semibold cursor-pointer select-none" onClick={() => handleSort('name')} data-testid="vh-sort-name">
                            Estimate <SortChevron field="name" />
                          </th>
                          <th className="px-4 py-2 font-semibold cursor-pointer select-none text-right" onClick={() => handleSort('pending')} data-testid="vh-sort-pending">
                            Pending <SortChevron field="pending" />
                          </th>
                          <th className="px-4 py-2 font-semibold cursor-pointer select-none text-right" onClick={() => handleSort('verified')} data-testid="vh-sort-verified">
                            Verified <SortChevron field="verified" />
                          </th>
                          <th className="px-4 py-2 font-semibold cursor-pointer select-none text-right" onClick={() => handleSort('pct')} data-testid="vh-sort-pct">
                            % Verified <SortChevron field="pct" />
                          </th>
                          <th className="px-4 py-2 font-semibold cursor-pointer select-none text-right" onClick={() => handleSort('days')} data-testid="vh-sort-days">
                            Last activity <SortChevron field="days" />
                          </th>
                          <th className="px-4 py-2 font-semibold text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortedEstimates.map((est) => (
                          <tr
                            key={est.id}
                            className="hover:bg-slate-50 transition-colors"
                            data-testid={`vh-row-${est.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800">
                                {est.name}
                                {est.stalled && (
                                  <Badge
                                    variant="outline"
                                    className="ml-2 border-rose-300 bg-rose-50 text-rose-700 text-[10px]"
                                    title={`No verification activity in ${data.stalled_days_threshold}+ days`}
                                    data-testid={`vh-stalled-${est.id}`}
                                  >
                                    <AlertTriangle className="w-3 h-3 mr-1" /> Stalled
                                  </Badge>
                                )}
                              </div>
                              {est.organization_name && (
                                <div className="text-[11px] text-slate-500 mt-0.5">{est.organization_name}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`tabular-nums ${est.imports_pending > 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                                {est.imports_pending}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-emerald-700 font-semibold tabular-nums">{est.imports_verified}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 justify-end">
                                <span className="text-[11px] font-medium text-slate-500 tabular-nums w-9 text-right">{est.pct_verified}%</span>
                                <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${est.pct_verified === 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`}
                                    style={{ width: `${est.pct_verified}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-[11px] text-slate-600 tabular-nums">
                              {est.days_since_last_verify == null
                                ? '—'
                                : (est.days_since_last_verify === 0 ? 'Today' : `${est.days_since_last_verify}d ago`)}
                              {est.last_verified_by && (
                                <div className="text-[10px] text-slate-400">by {est.last_verified_by}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => goToVerify(est.id)}
                                className="text-cyan-700 hover:bg-cyan-50 text-xs"
                                data-testid={`vh-open-${est.id}`}
                              >
                                Open <ArrowUpRight className="w-3 h-3 ml-1" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top verifiers */}
            {data.top_verifiers && data.top_verifiers.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-cyan-600" />
                    Top verifiers
                  </h2>
                  <div className="space-y-1.5" data-testid="vh-top-verifiers">
                    {data.top_verifiers.map((v, idx) => (
                      <div
                        key={v.name}
                        className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50 last:border-0"
                        data-testid={`vh-verifier-${idx}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 tabular-nums w-5">#{idx + 1}</span>
                          <span className="text-slate-700">{v.name}</span>
                        </div>
                        <span className="text-cyan-700 font-semibold tabular-nums">
                          {v.verifications.toLocaleString()} verifications
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
  );
}
